import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { captureClientSideError } from '@scale/frontend-lib/errors';
import { getWrapperComponentName } from '@scale/frontend-lib/util';

// TODO: Use new button
// eslint-disable-next-line import-path/forbidden
import Button from 'design-system/ButtonLegacy';
import { withUser } from 'react-scale/components/ui/UserContext';

const ErrorPlaceholder = withUser(
  // @ts-ignore
  ({ showReload, onReload, errorId, user, errorInfo, errorMessage, componentDisplayName }) => (
    <div className="error-container">
      <div className="icon">
        <FontAwesomeIcon style={{ fontSize: 40, opacity: 0.3 }} icon="exclamation" />
      </div>
      <div className="content">
        <h3>An unexpected error has occurred</h3>
        <p>Error ID: {errorId}</p>

        {user && user.isCorporateUser && (
          <React.Fragment>
            <p>
              <small>Only corp users can see this message</small>
              <strong>Error message: {errorMessage}</strong> <br />
              <strong>Component: {componentDisplayName}</strong>
            </p>
            {errorInfo && errorInfo.componentStack && (
              <p>
                <pre>{errorInfo.componentStack}</pre>
              </p>
            )}
          </React.Fragment>
        )}
      </div>

      <div className="actions">
        {showReload && (
          <div className="button">
            <Button type="success" size="sm" onClick={onReload}>
              Try to reload
            </Button>
          </div>
        )}
      </div>

      <style jsx>{`
        .error-container {
          padding: 15px 30px;
          margin-bottom: 15px;
          background: #fbfbfb;
          border-radius: 15px;
          display: flex;
          align-items: center;
        }

        .error-container .icon {
          width: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
        }

        .error-container .content {
          flex: 1;
        }

        .error-container .content h3 {
          margin: 0;
        }

        .error-container .content p {
          margin: 10px 0 0 0;
        }

        .error-container .content small {
          display: block;
          font-weight: bold;
          opacity: 0.6;
          text-transform: uppercase;
        }

        .error-container .content pre {
          width: 80%;
          max-height: 200px;
          overflow: scroll;
          background: rgba(255, 255, 255, 0.7);
        }

        .error-container .actions .button {
          margin-bottom: 10px;
        }
      `}</style>
    </div>
  ),
);

export interface ErrorPlaceholderProps {
  errorId: string | null;
  errorMessage: string | null;
  errorInfo: any;
  componentDisplayName: string;
  showReload: boolean;
  onReload: () => void;
}

// HACK: T = any until we convert more things to tsx (basically, anything that uses withErrorBoundary)
export const withErrorBoundary =
  ({
    reloadable = false,
    ErrorPlaceholderComponent = ErrorPlaceholder as React.ComponentType<ErrorPlaceholderProps>,
  } = {}) =>
  <T extends object>(WrappedComponent: React.ComponentType<T>): React.ComponentType<T> => {
    class WrapperComponent extends React.Component<T> {
      static WrappedComponent = WrappedComponent;

      static displayName = getWrapperComponentName('withErrorBoundary', WrappedComponent);

      state = { error: false, errorId: null, errorMessage: null, errorInfo: null };

      // @ts-ignore
      componentDidCatch(error, info) {
        const errorId = captureClientSideError(error, 'generic', info);

        this.setState({
          errorId,
          error: true,
          errorInfo: info,
          errorMessage: error && error.message,
        });
      }

      onReload = () => {
        this.setState({ error: false });
      };

      render() {
        const { error, errorId, errorMessage, errorInfo } = this.state;

        if (error) {
          return (
            <ErrorPlaceholderComponent
              errorId={errorId}
              errorMessage={errorMessage}
              errorInfo={errorInfo}
              componentDisplayName={
                WrappedComponent.displayName || WrappedComponent.name || 'unknow'
              }
              showReload={reloadable}
              onReload={this.onReload}
            />
          );
        }

        return <WrappedComponent {...this.props} />;
      }
    }

    hoistNonReactStatics(WrapperComponent, WrappedComponent);

    return WrapperComponent;
  };

export class ErrorBoundary extends React.Component<{ children?: React.ReactNode }> {
  state: {
    error?: boolean;
    errorInfo?: any;
    errorMessage?: string;
    errorId?: string;
  } = {};

  componentDidCatch(error: any, info: any) {
    const errorId = captureClientSideError(error, 'generic', info);

    this.setState({
      errorId,
      error: true,
      errorInfo: info,
      errorMessage: error && error.message,
    });
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorPlaceholder
          errorId={this.state.errorId}
          errorMessage={this.state.errorMessage}
          errorInfo={this.state.errorInfo}
          componentDisplayName="unknown"
        />
      );
    }
    return this.props.children;
  }
}
